import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  CFormLabel,
  CFormInput,
  CFormTextarea,
  CForm,
  CContainer,
  CRow,
  CCol,
  CFormCheck,
  CButton,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody
} from '@coreui/react';
import PropTypes from 'prop-types';
import axios from 'axios';

const WorkProgramTab = forwardRef(({ initialData, onSave }, ref) => {
  const [workStages, setWorkStages] = useState([]);
  const [selectedStageId, setSelectedStageId] = useState(null);
  
  // Модальное окно для справочника
  const [showModal, setShowModal] = useState(false);
  const [stagesData, setStagesData] = useState([]);
  const [selectedModalStageIndex, setSelectedModalStageIndex] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialData && initialData.workStages) {
      setWorkStages(initialData.workStages);
    }
  }, [initialData]);

  // Загрузка данных из API
  const fetchStagesData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/typetable/?ntype=89&id_parent=10746&table=typetable', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = Array.isArray(response.data) ? response.data : [];
      const sorted = data.sort((a, b) => a.Name.localeCompare(b.Name, undefined, { sensitivity: 'base' }));
      // Добавляем уникальный индекс к каждому элементу
      const dataWithIndex = sorted.map((item, index) => ({
        ...item,
        uniqueId: item.ID || `stage-${index}-${Date.now()}`
      }));
      setStagesData(dataWithIndex);
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      setStagesData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setShowModal(true);
    setSelectedModalStageIndex(null);
    fetchStagesData();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedModalStageIndex(null);
  };

  const handleStageDoubleClick = (stage) => {
    const newId = Math.max(...workStages.map(item => item.id), 0) + 1;
    const newNumber = workStages.length + 1;
    
    const newStage = {
      id: newId,
      number: newNumber,
      stageName: stage.Name,
      workName: '' // Пустое поле для наименования работ
    };
    
    const updatedStages = [...workStages, newStage];
    setWorkStages(updatedStages);
    setSelectedStageId(newId);
    handleCloseModal();
  };

  const handleStageClick = (id) => {
    setSelectedStageId(id);
  };

  const handleModalStageClick = (index) => {
    setSelectedModalStageIndex(index);
  };

  const handleAddStage = () => {
    handleOpenModal();
  };

  const handleRemoveStage = () => {
    if (selectedStageId && workStages.length > 0) {
      const confirmDelete = window.confirm('Удалить выбранный этап обследования?');
      
      if (confirmDelete) {
        const updatedStages = workStages.filter(item => item.id !== selectedStageId);
        
        // Перенумеровываем оставшиеся этапы
        const renumberedStages = updatedStages.map((item, index) => ({
          ...item,
          number: index + 1
        }));
        
        setWorkStages(renumberedStages);
        setSelectedStageId(renumberedStages.length > 0 ? renumberedStages[0].id : null);
      }
    } else if (workStages.length > 0) {
      const confirmDelete = window.confirm('Удалить последний этап обследования?');
      
      if (confirmDelete) {
        const updatedStages = workStages.slice(0, -1);
        
        // Перенумеровываем оставшиеся этапы
        const renumberedStages = updatedStages.map((item, index) => ({
          ...item,
          number: index + 1
        }));
        
        setWorkStages(renumberedStages);
        setSelectedStageId(renumberedStages.length > 0 ? renumberedStages[renumberedStages.length - 1].id : null);
      }
    }
  };

  const handleMoveUp = () => {
    if (selectedStageId && workStages.length > 1) {
      const index = workStages.findIndex(item => item.id === selectedStageId);
      if (index > 0) {
        const updatedStages = [...workStages];
        [updatedStages[index - 1], updatedStages[index]] = [updatedStages[index], updatedStages[index - 1]];
        
        // Перенумеровываем
        const renumberedStages = updatedStages.map((item, idx) => ({
          ...item,
          number: idx + 1
        }));
        
        setWorkStages(renumberedStages);
      }
    }
  };

  const handleMoveDown = () => {
    if (selectedStageId && workStages.length > 1) {
      const index = workStages.findIndex(item => item.id === selectedStageId);
      if (index < workStages.length - 1) {
        const updatedStages = [...workStages];
        [updatedStages[index], updatedStages[index + 1]] = [updatedStages[index + 1], updatedStages[index]];
        
        // Перенумеровываем
        const renumberedStages = updatedStages.map((item, idx) => ({
          ...item,
          number: idx + 1
        }));
        
        setWorkStages(renumberedStages);
      }
    }
  };

  const handleEditStage = () => {
    if (selectedStageId) {
      // Логика для редактирования этапа
      console.log('Редактирование этапа:', selectedStageId);
    }
  };

  const handleStageNameChange = (id, value) => {
    setWorkStages(workStages.map(item => 
      item.id === id ? { ...item, stageName: value } : item
    ));
  };

  const handleWorkNameChange = (id, value) => {
    setWorkStages(workStages.map(item => 
      item.id === id ? { ...item, workName: value } : item
    ));
  };

  const getFormDataForSave = () => {
    return {
      workStages
    };
  };

  useImperativeHandle(ref, () => ({
    save: async () => {
      if (onSave) {
        const formData = getFormDataForSave();
        await onSave(formData);
      }
    },
    getFormData: getFormDataForSave
  }));

  const handlePlanWorkClick = () => {
    console.log('План работ clicked');
  };

  return (
    <CContainer fluid className="work-program-tab-container">
      <CForm>
        {/* Кнопки управления */}
        <CRow className="mb-3">
          <CCol>
            <div className="d-flex gap-2">
              <CButton color="light" variant="outline" size="sm" className="text-dark border" onClick={handleAddStage}>
                Добавить
              </CButton>
              <CButton color="light" variant="outline" size="sm" className="text-dark border" onClick={handleRemoveStage}>
                Удалить
              </CButton>
              <CButton color="light" variant="outline" size="sm" className="text-dark border" onClick={handleEditStage}>
                Редактировать
              </CButton>
              <CButton color="light" variant="outline" size="sm" className="text-dark border" onClick={handleMoveUp}>
                Вверх
              </CButton>
              <CButton color="light" variant="outline" size="sm" className="text-dark border" onClick={handleMoveDown}>
                Вниз
              </CButton>
            </div>
          </CCol>
        </CRow>

        {/* Таблица этапов работ */}
        <CRow className="mb-3">
          <CCol>
            <CTable bordered small className="mb-0">
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell className="border" style={{ width: '50px' }}>№</CTableHeaderCell>
                  <CTableHeaderCell className="border">Наименование этапа обследования</CTableHeaderCell>
                  <CTableHeaderCell className="border">Наименование работ</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {workStages.map((item) => (
                  <CTableRow 
                    key={item.id} 
                    onClick={() => handleStageClick(item.id)}
                    className={selectedStageId === item.id ? 'table-primary' : ''}
                    style={{ cursor: 'pointer' }}
                  >
                    <CTableDataCell className="border" style={{ width: '50px', verticalAlign: 'middle' }}>
                      {item.number}
                    </CTableDataCell>
                    <CTableDataCell className="border p-0">
                      <CFormInput
                        type="text"
                        value={item.stageName}
                        onChange={(e) => handleStageNameChange(item.id, e.target.value)}
                        placeholder="Введите наименование этапа"
                        className="border-0 w-100"
                        style={{ borderRadius: '0' }}
                      />
                    </CTableDataCell>
                    <CTableDataCell className="border p-0">
                      <CFormInput
                        type="text"
                        value={item.workName}
                        onChange={(e) => handleWorkNameChange(item.id, e.target.value)}
                        placeholder="Введите наименование работ"
                        className="border-0 w-100"
                        style={{ borderRadius: '0' }}
                      />
                    </CTableDataCell>
                  </CTableRow>
                ))}
                {workStages.length === 0 && (
                  <CTableRow>
                    <CTableDataCell colSpan="3" className="text-center border py-3 text-muted">
                      Нет данных. Нажмите "Добавить" для выбора этапов обследования.
                    </CTableDataCell>
                  </CTableRow>
                )}
              </CTableBody>
            </CTable>
          </CCol>
        </CRow>
      </CForm>

      {/* Модальное окно справочника */}
      <CModal visible={showModal} onClose={handleCloseModal} size="lg">
        <CModalHeader>
          <CModalTitle>Справочник: Этапы обследования</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {/* Кнопки в модальном окне */}
          <div className="d-flex gap-2 mb-3">
            <CButton color="light" variant="outline" size="sm" className="text-dark border">
              Добавить
            </CButton>
            <CButton color="light" variant="outline" size="sm" className="text-dark border">
              Удалить
            </CButton>
            <CButton color="light" variant="outline" size="sm" className="text-dark border">
              Редактировать
            </CButton>
            <CButton 
              color="light" 
              variant="outline" 
              size="sm" 
              className="text-dark border"
              onClick={handlePlanWorkClick}
            >
              План работ
            </CButton>
          </div>

          {loading ? (
            <div className="text-center">Загрузка данных...</div>
          ) : (
            <>
              <CTable bordered small className="mb-3">
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell className="border">Наименование</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {stagesData.map((stage, index) => (
                    <CTableRow 
                      key={stage.uniqueId}
                      onDoubleClick={() => handleStageDoubleClick(stage)}
                      onClick={() => handleModalStageClick(index)}
                      className={selectedModalStageIndex === index ? 'table-primary' : ''}
                      style={{ cursor: 'pointer' }}
                    >
                      <CTableDataCell className="border">{stage.Name}</CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
              <div className="text-muted small">
                💡 Для добавления: двойной клик по строке
              </div>
            </>
          )}
        </CModalBody>
      </CModal>
    </CContainer>
  );
});

WorkProgramTab.propTypes = {
  initialData: PropTypes.shape({
    workStages: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        number: PropTypes.number.isRequired,
        stageName: PropTypes.string.isRequired,
        workName: PropTypes.string
      })
    )
  }),
  onSave: PropTypes.func
};

WorkProgramTab.defaultProps = {
  initialData: null,
  onSave: null
};

export default WorkProgramTab;